import React from "react";
import { useTranslation } from "react-i18next";
import { safeHtml } from "@/lib/safeHtml";
import { Upload, X } from "lucide-react";

/**
 * Step 1 of the simulate flow: photo upload + AI precheck.
 *
 * Encapsulates the dropzone, preview thumbnail, low-res warning banner and
 * the Gemini Vision precheck CTA. All state lives in the parent — we only
 * receive what we need to render + a small set of callbacks.
 */
export default function UploadStep({
  isInterior,
  fileRef,
  preview,
  onFileChange,
  onDrop,
  onRemove,
  lowResWarning,
  precheck,
  prechecking,
  onPrecheck,
}) {
  const { t } = useTranslation();

  return (
    <div className="border border-white/10 rounded-lg bg-zinc-900/30 p-6 md:p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-orange-500/15 text-orange-400 text-sm font-bold">
          1
        </div>
        <h3 className="font-outfit font-bold text-white text-xl">
          {t("simulate.step_1_title")}
        </h3>
      </div>

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        className={`relative cursor-pointer border-2 border-dashed rounded-lg transition-all duration-300 ${
          preview
            ? "border-orange-500/40 p-2"
            : "border-white/15 hover:border-orange-500 bg-zinc-950/50 p-12"
        }`}
        data-testid="upload-dropzone"
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*"
          className="hidden"
          onChange={(e) => onFileChange(e.target.files?.[0])}
          data-testid="upload-file-input"
        />
        {preview ? (
          <div className="relative">
            <img
              src={preview}
              alt="preview"
              className="w-full max-h-[420px] object-contain rounded-md"
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/70 border border-white/20 flex items-center justify-center text-white hover:bg-red-500/30 transition"
              data-testid="upload-remove-btn"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <div className="text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-orange-500/10 border border-orange-500/30 flex items-center justify-center mb-4">
              <Upload size={26} strokeWidth={1.5} className="text-orange-500" />
            </div>
            <div className="font-outfit text-lg font-bold text-white mb-1">
              {t("simulate.step_1_drag")}
            </div>
            <div className="text-sm text-zinc-500">
              {t("simulate.step_1_dropzone_hint", {
                type: isInterior
                  ? t("simulate.type_interior")
                  : t("simulate.type_exterior"),
              })}
            </div>
          </div>
        )}
      </div>

      {lowResWarning && preview && (
        <div
          className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300"
          data-testid="low-res-warning"
          dangerouslySetInnerHTML={safeHtml(t("simulate.low_res_warning"))}
        />
      )}

      {preview && (
        <div className="mt-3">
          <button
            type="button"
            onClick={onPrecheck}
            disabled={prechecking}
            className="w-full px-4 py-2.5 rounded-md border border-orange-500/40 bg-orange-500/10 hover:bg-orange-500/20 text-orange-300 text-xs font-bold uppercase tracking-wider transition disabled:opacity-50 disabled:cursor-wait"
            data-testid="precheck-btn"
          >
            {prechecking
              ? t("simulate.precheck_checking")
              : t("simulate.precheck_btn")}
          </button>
          {precheck && (
            <div
              className={`mt-2 rounded-md border px-3 py-2.5 text-xs ${
                precheck.proceed_recommended
                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                  : "border-amber-500/30 bg-amber-500/10 text-amber-200"
              }`}
              data-testid="precheck-result"
            >
              <div className="flex items-center justify-between mb-1">
                <div className="font-bold uppercase tracking-wider text-[10px]">
                  {precheck.proceed_recommended
                    ? t("simulate.precheck_ok")
                    : t("simulate.precheck_warn")}
                </div>
                <div className="font-mono text-[11px]">
                  {t("simulate.precheck_score")}{" "}
                  <span className="font-bold">{precheck.estimated_score}/5</span>
                </div>
              </div>
              {precheck.scene_type && (
                <div className="text-[10px] opacity-75 mb-1">
                  {t("simulate.precheck_detected")} {precheck.scene_type}
                </div>
              )}
              {precheck.warnings?.length > 0 && (
                <ul className="space-y-0.5 list-disc list-inside text-[11px] opacity-90 mt-1">
                  {precheck.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
